/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Rail desktop alineado al proto (teal ticketColors.gold, card Sucursal, hint Catálogo, tienda colapsada).
 */
import React, { Fragment, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import {
  useNavigation,
  useNavigationState,
  type NavigationProp,
  type ParamListBase,
} from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { webTealRailScrollBindings } from '../../shared/theme/webScrollbar';
import { ticketColors } from '../../shared/theme/ticketColors';
import { useSessionStore } from '../session/store/useSessionStore';
import { usePlanEntitlements } from '../providers/PlanEntitlementsProvider';
import { useSupportUnreadCount } from '../providers/SupportUnreadProvider';
import { usePendingOrdersCount } from '../providers/PendingOrdersCountProvider';
import {
  APP_SIDEBAR_WIDTH as SIDEBAR_WIDTH,
  getSidebarSections,
  isShellSidebarItemActive,
  type NavigationManifestItem,
} from './navigationManifest';
import { useNavVisibilityContext } from './useNavVisibilityContext';
import { navigateInShell, navigateRoot } from './rootNavigation';
import { navigateToManifestItemFromShell } from './navigateFromManifest';
import { AppBrandBlock } from './AppBrandBlock';
import { getOrganizationDisplayName } from './organizationDisplayName';
import { resolveSidebarActiveRoute } from './resolveSidebarActiveRoute';
import { useTabScreenRefreshUiOptional } from './TabScreenRefreshContext';
import { SupportUnreadBadge } from '../support/components/SupportUnreadBadge';
import { withSupportUnreadAccessibilityLabel } from '../support/components/supportUnreadPresentation';
import { OrganizationSwitcherSheet } from '../session/components/OrganizationSwitcherSheet';
import { useOrganizationSwitcherAccess } from '../session/hooks/useOrganizationSwitcherAccess';
import { SidebarBranchCard } from './SidebarBranchCard';
import { useStorefrontPublishBootstrap } from '../../onboarding/application/useStorefrontPublishBootstrap';

const APP_SHELL_ROUTE = 'AppShell';
const RAIL_TEAL = ticketColors.gold;
const RAIL_TEXT = 'rgba(255,255,255,0.78)';
const RAIL_TEXT_ON = '#fff';

function renderNavItems(
  items: NavigationManifestItem[],
  renderItem: (item: NavigationManifestItem) => React.ReactNode
) {
  return items.map((item) => <Fragment key={item.id}>{renderItem(item)}</Fragment>);
}

export function AppSidebar() {
  useStorefrontPublishBootstrap();
  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const currentBranch = useSessionStore((s) =>
    s.branches.find((b) => b.id === s.currentBranchId)
  );
  const { entitlements } = usePlanEntitlements();
  const { count: supportUnreadCount } = useSupportUnreadCount();
  const supportUnread = supportUnreadCount ?? 0;
  const { count: pendingOrdersCount, inboxBadge } = usePendingOrdersCount();
  const pendingOrders = pendingOrdersCount ?? 0;
  const ordersInboxBadge = inboxBadge ?? pendingOrders;
  const tabRefreshUi = useTabScreenRefreshUiOptional();
  const activeTabName = tabRefreshUi?.activeTabName;

  const appShellRoute = useNavigationState((state) =>
    state.routes.find((r) => r.name === APP_SHELL_ROUTE)
  );

  const activeRoute = resolveSidebarActiveRoute(
    appShellRoute as Parameters<typeof resolveSidebarActiveRoute>[0],
    activeTabName
  );

  const ctx = useNavVisibilityContext();

  const sections = useMemo(() => getSidebarSections(ctx), [ctx]);
  const organizationName = getOrganizationDisplayName(entitlements);
  const { canOpenOrganizationMenu } = useOrganizationSwitcherAccess();
  const [orgSwitcherOpen, setOrgSwitcherOpen] = useState(false);
  const quietZones = ctx.day1CollapseStorefrontNav;

  const openBranchSelector = () => navigateRoot(navigation, 'BranchSelector');

  return (
    <View style={styles.sidebar}>
      <View style={styles.brandBlock}>
        <AppBrandBlock
          tone="onTeal"
          organizationName={organizationName}
          onOrganizationPress={canOpenOrganizationMenu ? () => setOrgSwitcherOpen(true) : undefined}
        />
      </View>
      <OrganizationSwitcherSheet
        visible={orgSwitcherOpen}
        onClose={() => setOrgSwitcherOpen(false)}
        navigation={navigation}
      />
      <ScrollView {...webTealRailScrollBindings(styles.scroll)} contentContainerStyle={styles.scrollContent}>
        {currentBranch ? (
          <SidebarBranchCard branch={currentBranch} onPress={openBranchSelector} />
        ) : (
          <View style={styles.section}>
            <TouchableOpacity
              style={[styles.item, styles.branchAlertItem]}
              onPress={openBranchSelector}
              accessibilityRole="button"
              accessibilityLabel="Elegir sucursal"
            >
              <Ionicons name="business-outline" size={20} color={RAIL_TEXT_ON} />
              <Text style={[styles.itemLabel, styles.itemLabelOn]} numberOfLines={1}>
                Elegir sucursal
              </Text>
            </TouchableOpacity>
          </View>
        )}
        {sections.map((section) => {
          const dimSection =
            quietZones && (section.zone === 'account' || section.zone === 'negocio');
          return (
            <View key={section.zone} style={[styles.section, dimSection && styles.sectionQuiet]}>
              {section.items[0]?.label === section.label ? null : (
                <Text style={styles.sectionLabel}>{section.label}</Text>
              )}
              {section.collapsed ? (
                <View style={styles.collapseBox}>
                  <Text style={styles.collapseTitle}>Configurá tu tienda online</Text>
                  <Text style={styles.collapseHint}>
                    Esta sucursal todavía no tiene la tienda online activa.
                  </Text>
                  <TouchableOpacity
                    style={styles.collapseAction}
                    onPress={() => navigateInShell(navigation, 'StorefrontHub')}
                    accessibilityRole="button"
                    accessibilityLabel="Configurar tienda online"
                  >
                    <Text style={styles.collapseActionText}>Configurar tienda online</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                renderNavItems(section.items, (item) => {
                  const selected = isShellSidebarItemActive(item.route, activeRoute);
                  const showSupportBadge = item.route === 'SupportReports' && supportUnread > 0;
                  const showOrdersBadge = item.route === 'OrdersInbox' && ordersInboxBadge > 0;
                  const badgeCount = showSupportBadge
                    ? supportUnread
                    : showOrdersBadge
                      ? ordersInboxBadge
                      : 0;
                  // El badge de la bandeja suma pedidos + reclamos + mensajes +
                  // consultas + cotizaciones — copy genérico, no "pedidos".
                  const a11yLabel = showOrdersBadge
                    ? `${item.label}, ${badgeCount} sin atender`
                    : withSupportUnreadAccessibilityLabel(item.label, badgeCount);
                  const iconColor = selected ? RAIL_TEXT_ON : RAIL_TEXT;
                  return (
                    <TouchableOpacity
                      style={[styles.item, selected && styles.itemSelected]}
                      onPress={() => navigateToManifestItemFromShell(navigation, item)}
                      accessibilityRole="button"
                      accessibilityState={{ selected }}
                      accessibilityLabel={a11yLabel}
                    >
                      <Ionicons name={item.icon} size={20} color={iconColor} />
                      <Text
                        style={[styles.itemLabel, selected && styles.itemLabelOn]}
                        numberOfLines={2}
                      >
                        {item.label}
                      </Text>
                      {showSupportBadge ? (
                        <SupportUnreadBadge count={supportUnread} variant="inline" style={styles.railBadge} />
                      ) : null}
                      {showOrdersBadge ? (
                        <SupportUnreadBadge
                          count={ordersInboxBadge}
                          variant="inline"
                          style={styles.railBadge}
                        />
                      ) : null}
                    </TouchableOpacity>
                  );
                })
              )}
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  sidebar: {
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    alignSelf: 'stretch',
    height: '100%' as unknown as number,
    minHeight: 0,
    flexDirection: 'column',
    backgroundColor: RAIL_TEAL,
  },
  brandBlock: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: 'rgba(255,255,255,0.18)',
  },
  scroll: { flex: 1 },
  scrollContent: { paddingTop: spacing.sm, paddingBottom: spacing.xxl + spacing.lg },
  section: { marginTop: spacing.md, paddingHorizontal: spacing.sm },
  sectionQuiet: { opacity: 0.55 },
  sectionLabel: {
    ...typography.caption,
    color: 'rgba(255,255,255,0.65)',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: spacing.xs,
    paddingHorizontal: spacing.xs,
  },
  collapseBox: {
    marginHorizontal: spacing.xs,
    marginTop: 2,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: 'rgba(255,255,255,0.35)',
  },
  collapseTitle: {
    ...typography.caption,
    fontWeight: '700',
    color: RAIL_TEXT_ON,
    marginBottom: 4,
  },
  collapseHint: {
    ...typography.caption,
    color: 'rgba(255,255,255,0.85)',
    lineHeight: 18,
  },
  collapseAction: {
    marginTop: 8,
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: RAIL_TEAL,
  },
  collapseActionText: {
    ...typography.caption,
    color: RAIL_TEXT_ON,
    fontWeight: '700',
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 9,
    paddingHorizontal: 12,
    borderRadius: 10,
    marginBottom: 2,
  },
  itemSelected: {
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  branchAlertItem: {
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.45)',
  },
  itemLabel: {
    ...typography.body,
    color: RAIL_TEXT,
    flex: 1,
    minWidth: 0,
    fontWeight: '600',
  },
  itemLabelOn: {
    color: RAIL_TEXT_ON,
    fontWeight: '700',
  },
  railBadge: {
    backgroundColor: '#FCA5A5',
  },
});
